import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { darkTheme, lightTheme, type ThemeTokens } from "../theme";

const STORAGE_KEY = "wsdadmin.dark";

type ThemeState = {
  dark: boolean;
  toggleDark: () => void;
  t: ThemeTokens;
};

const ThemeCtx = createContext<ThemeState | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [dark, setDark] = useState<boolean>(() => localStorage.getItem(STORAGE_KEY) === "1");

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, dark ? "1" : "0");
  }, [dark]);

  const toggleDark = useCallback(() => setDark(d => !d), []);

  const value = useMemo<ThemeState>(
    () => ({ dark, toggleDark, t: dark ? darkTheme : lightTheme }),
    [dark, toggleDark],
  );

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
